// Scenario fitter: fits the single scenario template ("task force attacks to seize an objective while
// the enemy defends in depth", spec §2) onto any AO (area of operations) grid. See spec §3-§5 and plan
// Task 6.

import { Grid } from './grid';
import { Rng } from './rng';
import type { Dials, Graphic, Placement, Scenario, UnitDef, UnitType } from './types';
import unitsJson from '../../data/units.json';
import template from '../../data/scenario-template.json';

export const UNIT_DEFS: Record<UnitType, UnitDef> = unitsJson as unknown as Record<UnitType, UnitDef>;

export function unitDefOf(type: UnitType): UnitDef {
  return UNIT_DEFS[type];
}

export function graphicById(scenario: Scenario, id: string): Graphic | undefined {
  return scenario.graphics.find((g) => g.id === id);
}

// ---- Task organization ---------------------------------------------------------------------------

interface RosterEntry { type: UnitType; callsign: string }
interface CompanySpec { type: UnitType; companies: number; platoonsPerCompany: number }

// Blue: 2x armor company (3 plt), 2x mech company (3 plt), 1x inf company (3 plt) = 15 maneuver
// platoons, plus 9 supporting/enabling units (recon, arty, mortar, UAS, HQ, log) = 24 total.
const BLUE_COMPANIES: CompanySpec[] = [
  { type: 'armor', companies: 2, platoonsPerCompany: 3 },
  { type: 'mech', companies: 2, platoonsPerCompany: 3 },
  { type: 'inf', companies: 1, platoonsPerCompany: 3 },
];

const BLUE_SUPPORT: { type: UnitType; count: number; prefix: string }[] = [
  { type: 'recon', count: 2, prefix: 'SCT' },
  { type: 'spArty', count: 1, prefix: 'FA' },
  { type: 'mortar', count: 1, prefix: 'MTR' },
  { type: 'uas', count: 2, prefix: 'UAV' },
  { type: 'hq', count: 1, prefix: 'HQ' },
  { type: 'log', count: 2, prefix: 'LOG' },
];

function blueRoster(): RosterEntry[] {
  const roster: RosterEntry[] = [];
  let letterCode = 'A'.charCodeAt(0);
  for (const spec of BLUE_COMPANIES) {
    for (let c = 0; c < spec.companies; c++) {
      const letter = String.fromCharCode(letterCode++);
      for (let p = 1; p <= spec.platoonsPerCompany; p++) {
        // Callsign convention: company letter + platoon number + lead-element digit, e.g. "A11" =
        // Company A, 1st platoon.
        roster.push({ type: spec.type, callsign: `${letter}${p}1` });
      }
    }
  }
  for (const s of BLUE_SUPPORT) {
    for (let i = 1; i <= s.count; i++) {
      roster.push({ type: s.type, callsign: s.count > 1 ? `${s.prefix}${i}` : s.prefix });
    }
  }
  return roster;
}

// Red: 3 armor, 4 mech, 3 inf, 2 recon, 1 spArty, 1 mortar, 1 uas, 1 hq, 2 log = 18.
const RED_COMPOSITION: { type: UnitType; count: number }[] = [
  { type: 'armor', count: 3 },
  { type: 'mech', count: 4 },
  { type: 'inf', count: 3 },
  { type: 'recon', count: 2 },
  { type: 'spArty', count: 1 },
  { type: 'mortar', count: 1 },
  { type: 'uas', count: 1 },
  { type: 'hq', count: 1 },
  { type: 'log', count: 2 },
];

function redRoster(): RosterEntry[] {
  const roster: RosterEntry[] = [];
  let n = 1;
  for (const spec of RED_COMPOSITION) {
    for (let i = 0; i < spec.count; i++) roster.push({ type: spec.type, callsign: `R${n++}` });
  }
  return roster;
}

// ---- Grid axis abstraction -------------------------------------------------------------------------
// The fitter reasons in "depth" (blue's direction of advance) and "width" (perpendicular) so the same
// logic handles a west->east or a south->north corridor.

type Orientation = 'WE' | 'SN';

interface Axis {
  readonly orientation: Orientation;
  readonly depthLen: number;
  readonly widthLen: number;
  cellAt(depthIdx: number, widthIdx: number): number;
}

function axisFor(grid: Grid, orientation: Orientation): Axis {
  if (orientation === 'WE') {
    return { orientation, depthLen: grid.w, widthLen: grid.h, cellAt: (d, w) => grid.index(d, w) };
  }
  return { orientation, depthLen: grid.h, widthLen: grid.w, cellAt: (d, w) => grid.index(w, d) };
}

function stripPassableCount(grid: Grid, axis: Axis, depthStart: number, depthSpan: number): number {
  let n = 0;
  const end = Math.min(axis.depthLen, depthStart + depthSpan);
  for (let d = Math.max(0, depthStart); d < end; d++) {
    for (let w = 0; w < axis.widthLen; w++) if (grid.passable(axis.cellAt(d, w))) n++;
  }
  return n;
}

/** Picks W->E or S->N by whichever pair of opposite edge strips has more passable cells. */
function chooseOrientation(grid: Grid): Orientation {
  const stripDepth = Math.max(1, Math.round(Math.min(grid.w, grid.h) * 0.02));
  const we = axisFor(grid, 'WE');
  const sn = axisFor(grid, 'SN');
  const weCount = stripPassableCount(grid, we, 0, stripDepth) + stripPassableCount(grid, we, we.depthLen - stripDepth, stripDepth);
  const snCount = stripPassableCount(grid, sn, 0, stripDepth) + stripPassableCount(grid, sn, sn.depthLen - stripDepth, stripDepth);
  return weCount >= snCount ? 'WE' : 'SN';
}

interface Band { start: number; end: number; depthIdx: number }

/**
 * The longest run of passable width-indices at `depthIdx`; if that exact line has none, searches up to
 * `maxSearch` steps in `dir` (1 = toward the far edge, -1 = toward the start edge) so a placement never
 * lands on impassable ground.
 */
function passableBandNear(grid: Grid, axis: Axis, depthIdx: number, maxSearch: number, dir: 1 | -1): Band | null {
  for (let step = 0; step <= maxSearch; step++) {
    const dIdx = depthIdx + dir * step;
    if (dIdx < 0 || dIdx >= axis.depthLen) continue;
    let bestStart = -1, bestLen = 0, curStart = -1, curLen = 0;
    for (let w = 0; w < axis.widthLen; w++) {
      if (grid.passable(axis.cellAt(dIdx, w))) {
        if (curLen === 0) curStart = w;
        curLen++;
        if (curLen > bestLen) { bestLen = curLen; bestStart = curStart; }
      } else curLen = 0;
    }
    if (bestLen > 0) return { start: bestStart, end: bestStart + bestLen - 1, depthIdx: dIdx };
  }
  return null;
}

/** All passable cells with depth index in [d0, d1] inclusive, optionally further filtered. */
function zoneCells(grid: Grid, axis: Axis, d0: number, d1: number, filter?: (cell: number) => boolean): number[] {
  const lo = Math.max(0, Math.min(d0, d1));
  const hi = Math.min(axis.depthLen - 1, Math.max(d0, d1));
  const out: number[] = [];
  for (let dd = lo; dd <= hi; dd++) {
    for (let w = 0; w < axis.widthLen; w++) {
      const cell = axis.cellAt(dd, w);
      if (grid.passable(cell) && (!filter || filter(cell))) out.push(cell);
    }
  }
  return out;
}

/** Passable cells with depth index in [d0, d1] AND width index in [w0, w1] (both inclusive). */
function zoneCellsBand(grid: Grid, axis: Axis, d0: number, d1: number, w0: number, w1: number): number[] {
  const lo = Math.max(0, Math.min(d0, d1));
  const hi = Math.min(axis.depthLen - 1, Math.max(d0, d1));
  const out: number[] = [];
  for (let dd = lo; dd <= hi; dd++) {
    for (let w = Math.max(0, w0); w <= Math.min(axis.widthLen - 1, w1); w++) {
      const cell = axis.cellAt(dd, w);
      if (grid.passable(cell)) out.push(cell);
    }
  }
  return out;
}

function elevTopQuartileFilter(grid: Grid, cells: number[]): (cell: number) => boolean {
  if (cells.length === 0) return () => false;
  const elevs = cells.map((c) => grid.elevAt(c)).sort((a, b) => a - b);
  const threshold = elevs[Math.min(Math.floor(elevs.length * 0.75), elevs.length - 1)];
  return (cell) => grid.elevAt(cell) >= threshold;
}

function shuffle<T>(arr: T[], rng: Rng): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    const t = a[i]; a[i] = a[j]; a[j] = t;
  }
  return a;
}

// ---- Advance axis / corridor ------------------------------------------------------------------------
// Red placement (every posture) is constrained to a corridor around the line from the assembly area to
// the objective, so blue's straight drive down the axis always meets resistance instead of finding red
// bunched in a corner off the line of advance.

interface AxisLine { ax: number; ay: number; bx: number; by: number }

/** The width-axis depth index of a cell (the coordinate `axisFor` calls "depth"), orientation-aware. */
function depthIndexOf(grid: Grid, orientation: Orientation, cell: number): number {
  return orientation === 'WE' ? grid.cx(cell) : grid.cy(cell);
}

/** Perpendicular distance in meters from a cell's center to the (infinite) axis line. */
function perpDistM(grid: Grid, line: AxisLine, cell: number): number {
  const p = grid.centerCm(cell);
  const abx = line.bx - line.ax, aby = line.by - line.ay;
  const apx = p.x - line.ax, apy = p.y - line.ay;
  const abLen = Math.sqrt(abx * abx + aby * aby) || 1;
  const cross = Math.abs(abx * apy - aby * apx);
  return cross / abLen / 100;
}

function corridorFilter(grid: Grid, cells: number[], line: AxisLine, maxM: number): number[] {
  return cells.filter((c) => perpDistM(grid, line, c) <= maxM);
}

/** Cells within `cells` restricted to the corridor, widening from 2.5 km to 4 km to unlimited until at
 * least `minNeeded` candidates are available (so a placement never starves for lack of room). */
function corridorZone(grid: Grid, cells: number[], line: AxisLine, minNeeded: number): number[] {
  const tight = corridorFilter(grid, cells, line, 2500);
  if (tight.length >= minNeeded) return tight;
  const wide = corridorFilter(grid, cells, line, 4000);
  if (wide.length >= minNeeded) return wide;
  return cells;
}

/** Combat-capable red types eligible to be repositioned to satisfy the blocking guarantee below. */
const BLOCKING_ELIGIBLE_TYPES: ReadonlySet<UnitType> = new Set(['armor', 'mech', 'inf', 'recon', 'spArty', 'mortar']);

/**
 * Regardless of posture, guarantees a straight blue drive down the axis meets resistance: at least 6
 * red units within 1.5 km of the axis between PL WHITE and PL BLUE, and at least 2 red units (any type)
 * within 1 km of the objective center. Relocates the minimum number of already-placed units needed to
 * close each gap, preferring not to disturb units that already satisfy the other guarantee.
 */
function enforceBlockingGuarantee(
  grid: Grid,
  axis: Axis,
  axisLine: AxisLine,
  d50: number,
  d75: number,
  objPt: { x: number; y: number },
  red: RosterEntry[],
  redCellByCallsign: Map<string, number>,
  used: Set<number>,
  rng: Rng,
): void {
  const beltOk = (cell: number): boolean => {
    const d = depthIndexOf(grid, axis.orientation, cell);
    return d >= d50 && d <= d75 && perpDistM(grid, axisLine, cell) <= 1500;
  };
  const objOk = (cell: number): boolean => grid.distCm(grid.centerCm(cell), objPt) <= 100000; // 1 km

  const currentCount = (check: (cell: number) => boolean): { count: number; callsigns: Set<string> } => {
    let count = 0;
    const callsigns = new Set<string>();
    for (const u of red) {
      const cell = redCellByCallsign.get(u.callsign);
      if (cell !== undefined && check(cell)) { count++; callsigns.add(u.callsign); }
    }
    return { count, callsigns };
  };

  const relocate = (callsign: string, cell: number): void => {
    const prev = redCellByCallsign.get(callsign);
    if (prev !== undefined) used.delete(prev);
    redCellByCallsign.set(callsign, cell);
    used.add(cell);
  };

  const BELT_NEED = 6;
  const belt = currentCount(beltOk);
  if (belt.count < BELT_NEED) {
    const need = BELT_NEED - belt.count;
    const beltPool = zoneCells(grid, axis, d50, d75).filter((c) => perpDistM(grid, axisLine, c) <= 1500 && !used.has(c));
    const pool = shuffle(beltPool, rng);
    const movers = shuffle(red.filter((u) => BLOCKING_ELIGIBLE_TYPES.has(u.type) && !belt.callsigns.has(u.callsign)), rng);
    let taken = 0;
    for (const u of movers) {
      if (taken >= need || taken >= pool.length) break;
      relocate(u.callsign, pool[taken]);
      taken++;
    }
  }

  const OBJ_NEED = 2;
  const beltAfter = currentCount(beltOk);
  const objState = currentCount(objOk);
  if (objState.count < OBJ_NEED) {
    const need = OBJ_NEED - objState.count;
    const objPool: number[] = [];
    for (let i = 0; i < grid.w * grid.h; i++) {
      if (grid.passable(i) && !used.has(i) && objOk(i)) objPool.push(i);
    }
    const pool = shuffle(objPool, rng);
    const preferred = shuffle(red.filter((u) => !objState.callsigns.has(u.callsign) && !beltAfter.callsigns.has(u.callsign)), rng);
    const fallback = shuffle(red.filter((u) => !objState.callsigns.has(u.callsign) && beltAfter.callsigns.has(u.callsign)), rng);
    const movers = [...preferred, ...fallback];
    let taken = 0;
    for (const u of movers) {
      if (taken >= need || taken >= pool.length) break;
      relocate(u.callsign, pool[taken]);
      taken++;
    }
  }
}

/** Picks `count` distinct cells not already in `used`, trying candidate lists in order (later lists are broader fallbacks so a placement always finds somewhere passable). */
function pickCells(rng: Rng, used: Set<number>, count: number, ...candidateLists: number[][]): number[] {
  const picked: number[] = [];
  for (const list of candidateLists) {
    if (picked.length >= count) break;
    for (const c of shuffle(list, rng)) {
      if (picked.length >= count) break;
      if (!used.has(c)) { picked.push(c); used.add(c); }
    }
  }
  return picked;
}

function pickOne<T>(rng: Rng, list: T[]): T {
  return list[rng.int(list.length)];
}

// ---- Fitter -----------------------------------------------------------------------------------------

export function fitScenario(grid: Grid, seed: number, dials: Dials): Scenario {
  const rng = new Rng(seed);
  const tf = pickOne(rng, template.tfNames);
  const objName = pickOne(rng, template.objNames);
  const enemy = pickOne(rng, template.enemyDescriptions);

  const orientation = chooseOrientation(grid);
  const axis = axisFor(grid, orientation);
  const dFar = axis.depthLen - 1;

  const depthAt = (frac: number) => Math.round(frac * dFar);
  const d25 = depthAt(0.25), d50 = depthAt(0.5), d75 = depthAt(0.75);

  const twoKmCells = Math.max(1, Math.round(2000 / grid.cellM));

  // Last-resort fallback so a placement always finds a passable cell even on unusual terrain.
  const allPassable: number[] = [];
  for (let i = 0; i < grid.w * grid.h; i++) if (grid.passable(i)) allPassable.push(i);

  const used = new Set<number>();

  // -- Assembly area: a strip 2 km deep on the start edge, centered on the widest passable band there.
  const assemblyDepthEnd = Math.min(dFar, twoKmCells - 1);
  const assemblyBand = passableBandNear(grid, axis, 0, dFar, 1) ?? { start: 0, end: axis.widthLen - 1, depthIdx: 0 };
  const assemblyZone = zoneCellsBand(grid, axis, 0, assemblyDepthEnd, assemblyBand.start, assemblyBand.end);

  // -- Objective: an 800 m radius circle on the far edge, on the best-concealment/elevation passable cluster.
  const objBand = passableBandNear(grid, axis, dFar, dFar, -1) ?? { start: 0, end: axis.widthLen - 1, depthIdx: dFar };
  const objCenterW = Math.round((objBand.start + objBand.end) / 2);
  const objCell = axis.cellAt(objBand.depthIdx, objCenterW);
  const objPt = grid.centerCm(objCell);

  // -- Phase lines: PL RED / WHITE / BLUE at 25/50/75% depth, spanning the full width.
  function plGraphic(id: string, name: string, depthIdx: number): Graphic {
    const a = grid.centerCm(axis.cellAt(depthIdx, 0));
    const b = grid.centerCm(axis.cellAt(depthIdx, axis.widthLen - 1));
    return { id, kind: 'pl', name, pts: [a, b] };
  }
  const plRed = plGraphic('pl-red', 'PL RED', d25);
  const plWhite = plGraphic('pl-white', 'PL WHITE', d50);
  const plBlue = plGraphic('pl-blue', 'PL BLUE', d75);

  // -- NAIs: one chokepoint (narrowest passable width) per segment between the assembly area, PL RED,
  //    PL WHITE, PL BLUE, and the objective (four segments -> four NAIs), radius 400 m.
  const segments: [number, number][] = [[0, d25], [d25, d50], [d50, d75], [d75, dFar]];
  const nais: Graphic[] = segments.map(([lo, hi], i) => {
    let bestD = lo, bestWidth = Infinity;
    for (let dIdx = lo; dIdx <= hi; dIdx++) {
      let width = 0;
      for (let w = 0; w < axis.widthLen; w++) if (grid.passable(axis.cellAt(dIdx, w))) width++;
      if (width > 0 && width < bestWidth) { bestWidth = width; bestD = dIdx; }
    }
    const span = Math.max(1, hi - lo);
    const band = passableBandNear(grid, axis, bestD, span, 1) ?? passableBandNear(grid, axis, bestD, span, -1);
    const centerW = band ? Math.round((band.start + band.end) / 2) : Math.round(axis.widthLen / 2);
    const centerD = band ? band.depthIdx : bestD;
    const pt = grid.centerCm(axis.cellAt(centerD, centerW));
    return { id: `nai-${i + 1}`, kind: 'nai', name: `NAI ${i + 1}`, pts: [pt], radiusM: 400 };
  });

  // -- Assembly area graphic: a rectangle from the start edge to 2 km in, spanning the band.
  const aaCorners = [
    grid.centerCm(axis.cellAt(0, assemblyBand.start)),
    grid.centerCm(axis.cellAt(assemblyDepthEnd, assemblyBand.start)),
    grid.centerCm(axis.cellAt(assemblyDepthEnd, assemblyBand.end)),
    grid.centerCm(axis.cellAt(0, assemblyBand.end)),
  ];
  const aa: Graphic = { id: 'aa', kind: 'aa', name: `AA ${tf}`, pts: aaCorners };
  const obj: Graphic = { id: 'obj', kind: 'obj', name: `OBJ ${objName}`, pts: [objPt], radiusM: 800 };

  const graphics: Graphic[] = [aa, obj, plRed, plWhite, plBlue, ...nais];

  // -- Blue placement: 24 units scattered across the assembly area zone, no two on the same cell.
  const blue = blueRoster();
  const bluePicks = pickCells(rng, used, blue.length, assemblyZone, allPassable);
  const bluePlacements: Placement[] = blue.map((u, i) => {
    const pt = grid.centerCm(bluePicks[i]);
    return { type: u.type, callsign: u.callsign, x: pt.x, y: pt.y };
  });

  // -- Advance axis: assembly-area centroid to the objective center. Red placement (every posture) is
  // restricted to a corridor around this line (see corridorZone) so it always sits astride blue's line
  // of advance instead of bunched in a corner off-axis.
  const assemblyCentroid = {
    x: Math.round(aaCorners.reduce((s, p) => s + p.x, 0) / aaCorners.length),
    y: Math.round(aaCorners.reduce((s, p) => s + p.y, 0) / aaCorners.length),
  };
  const axisLine: AxisLine = { ax: assemblyCentroid.x, ay: assemblyCentroid.y, bx: objPt.x, by: objPt.y };

  // -- Red placement: by posture, generally on defensible cells between PL WHITE and the objective.
  const red = redRoster();
  const redCellByCallsign = new Map<string, number>();
  const assign = (entries: RosterEntry[], cells: number[]) => {
    for (let i = 0; i < entries.length && i < cells.length; i++) redCellByCallsign.set(entries[i].callsign, cells[i]);
  };

  const corridorAllPassable = corridorZone(grid, allPassable, axisLine, red.length);

  if (dials.red === 'screen') {
    // Recon screens on PL RED; the rest of the force holds the defensible zone between WHITE and the objective.
    const reconEntries = red.filter((u) => u.type === 'recon');
    const restEntries = red.filter((u) => u.type !== 'recon');
    const reconZoneAll = zoneCells(grid, axis, Math.max(0, d25 - 1), Math.min(dFar, d25 + 1));
    const reconZone = corridorZone(grid, reconZoneAll, axisLine, reconEntries.length);
    const restZoneAll = zoneCells(grid, axis, d50, dFar);
    const restZone = corridorZone(grid, restZoneAll, axisLine, restEntries.length);
    assign(reconEntries, pickCells(rng, used, reconEntries.length, reconZone, restZone, corridorAllPassable, allPassable));
    assign(restEntries, pickCells(rng, used, restEntries.length, restZone, corridorAllPassable, allPassable));
  } else if (dials.red === 'forward') {
    // Defend forward: mass the whole force on PL RED.
    const bandZoneAll = zoneCells(grid, axis, Math.max(0, d25 - 2), Math.min(dFar, d25 + 2));
    const bandZone = corridorZone(grid, bandZoneAll, axisLine, red.length);
    assign(red, pickCells(rng, used, red.length, bandZone, corridorAllPassable, allPassable));
  } else {
    // Defend in depth (default): main body on cover >= 120 or top-quartile elevation overlooking the
    // corridor; a reserve (the armor) staged 2 km further back, toward the objective.
    const reserveEntries = red.filter((u) => u.type === 'armor');
    const mainEntries = red.filter((u) => u.type !== 'armor');
    const mainDepthEnd = Math.max(d50, dFar - twoKmCells);
    const reserveDepthStart = Math.min(dFar, mainDepthEnd + 1);
    const rawMainAll = zoneCells(grid, axis, d50, mainDepthEnd);
    const rawMain = corridorZone(grid, rawMainAll, axisLine, mainEntries.length);
    const topQuartile = elevTopQuartileFilter(grid, rawMain);
    const defensible = rawMain.filter((c) => grid.coverAt(c) >= 120 || topQuartile(c));
    const reserveZoneAll = zoneCells(grid, axis, reserveDepthStart, dFar);
    const reserveZone = corridorZone(grid, reserveZoneAll, axisLine, reserveEntries.length);
    assign(mainEntries, pickCells(rng, used, mainEntries.length, defensible, rawMain, corridorAllPassable, allPassable));
    assign(reserveEntries, pickCells(rng, used, reserveEntries.length, reserveZone, rawMainAll, corridorAllPassable, allPassable));
  }

  // -- Blocking guarantee: no posture is allowed to leave the axis empty.
  enforceBlockingGuarantee(grid, axis, axisLine, d50, d75, objPt, red, redCellByCallsign, used, rng);

  const redPlacements: Placement[] = red.map((u) => {
    const cell = redCellByCallsign.get(u.callsign) ?? pickCells(rng, used, 1, allPassable)[0];
    const pt = grid.centerCm(cell);
    return { type: u.type, callsign: u.callsign, x: pt.x, y: pt.y };
  });

  return {
    ao: grid.meta.name,
    seed,
    blue: bluePlacements,
    red: redPlacements,
    graphics,
    objectiveId: obj.id,
    assemblyId: aa.id,
    names: { tf, obj: objName, enemy },
    timeLimitMin: 240,
  };
}
